import React from 'react';

import classNames from 'classnames';

/**
 * Background/text pairs for the initials chip, all drawn from the Tailwind colour tokens.
 * `chipColour` indexes into this deterministically by owner id, so a given person keeps
 * the same colour across rows and renders.
 */
const CHIP_PALETTE = [
    'tw-bg-blue-15 tw-text-dark-blue',
    'tw-bg-green-25 tw-text-charcoal-75',
    'tw-bg-red-25 tw-text-charcoal',
    'tw-bg-blue-25 tw-text-dark-blue',
    'tw-bg-charcoal-15 tw-text-charcoal-75',
];

const chipColour = (ownerId) => CHIP_PALETTE[Math.abs(Number(ownerId) || 0) % CHIP_PALETTE.length];

/**
 * Initials for a person's name: first character of the first word plus first character of the
 * last word, e.g. "Jean Dubois" → "JD", "Jean Claude Dubois" → "JD", "Water" → "W".
 * Script-agnostic on purpose — Arabic and Cyrillic names must produce initials too, not a blank
 * chip that would read as "unassigned".
 */
const initialsFor = (name) => {
    const words = (name || '').trim().split(/\s+/).filter(Boolean);

    if (words.length === 0) {
        return '';
    }

    const first = words[0][0];
    const last = words.length > 1 ? words[words.length - 1][0] : '';

    return `${first}${last}`.toLocaleUpperCase();
};

// 26px at 11px/600 — the wireframe's `.avatar`. It fits the 72px Owner column, whose width is
// set by the "Owner" heading rather than by the chip.
const CHIP_CLASSES = 'tw-flex tw-h-[26px] tw-w-[26px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-semibold';

/**
 * Read-only presentation of a row's owner: the initials chip on its own, with the full name on
 * hover. The name is not repeated beside it — at plan scale the column reads as a column of
 * people, and the chip carries that at a fraction of the width the spelled-out names needed.
 *
 * An unowned row gets a neutral chip rather than a blank cell, so "nobody owns this" is a state
 * you can see down the column instead of an absence you have to notice.
 */
const OwnerChip = ({ name, ownerId = null }) => {
    // Whether the row has an owner is decided by the name, never by the derived initials —
    // a name we cannot abbreviate is still an assigned owner.
    if (!name?.trim()) {
        const unassigned = $t('Unassigned');

        return (
            <span className={classNames(CHIP_CLASSES, 'tw-bg-charcoal-15 tw-text-charcoal-50')} title={unassigned} role="img" aria-label={unassigned}>
                &mdash;
            </span>
        );
    }

    return (
        <span className={classNames(CHIP_CLASSES, chipColour(ownerId))} title={name} role="img" aria-label={name}>
            {initialsFor(name)}
        </span>
    );
};

export default OwnerChip;
